import {
  Box,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { CampaignStepOrders, CampaignStepTitles } from "../schema";

interface Props {
  activeStep: number;
  handleClose: () => void;
  title?: string;
}

const StepperHeader = ({ activeStep, handleClose, title }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  if (isMobileScreen) {
    return (
      <Grid
        container
        justifyContent="center"
        sx={{ mt: 3 }}
        flexDirection={"column"}
        alignItems="center"
      >
        <Box
          sx={{
            py: 1,
            px: 2,
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 8,
            textAlign: "center",
            fontSize: 12,
          }}
        >
          Step {activeStep + 1}/{CampaignStepOrders.length}
        </Box>

        <Typography variant="h5" textAlign={"center"} sx={{ mt: 2, mb: 0 }}>
          {CampaignStepTitles[CampaignStepOrders[activeStep]]}
        </Typography>
      </Grid>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 2.5, md: 4 },
        pt: { xs: 3, md: 4 },
        pb: 3,
        position: "relative",
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          width: 25,
          height: 25,
        }}
      >
        <Box
          component="i"
          className="fa-regular fa-xmark"
          sx={{ fontSize: 20 }}
        />
      </IconButton>

      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: { xs: 20, md: 24 },
            fontWeight: 700,
            lineHeight: 1.3,
          }}
        >
          {title || "Create New Campaign"}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#4A5565", mt: 0.5 }}>
          Follow these simple steps to launch your campaign
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: { xs: "100%", md: 520 },
        }}
      >
        {CampaignStepOrders.map((label, index) => {
          const isCompleted = index < activeStep;
          const isActive = index === activeStep;
          const isLast = index === CampaignStepOrders.length - 1;

          return (
            <Box
              key={label}
              sx={{
                display: "flex",
                alignItems: "center",
                flex: isLast ? 0 : 1,
              }}
            >
              {/* Circle + label */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 35,
                    height: 35,
                    borderRadius: "50%",
                    bgcolor:
                      isCompleted || isActive ? "primary.main" : "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {isCompleted ? (
                    <Box
                      component="i"
                      className="fa-solid fa-check"
                      sx={{ fontSize: 12, color: "white" }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: isActive ? "white" : "grey.500",
                      }}
                    >
                      {index + 1}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "primary.main" : "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {CampaignStepTitles[label]}
                </Typography>
              </Box>
              {/* Divider line between circles */}
              {!isLast && (
                <Box
                  sx={{
                    height: "1px",
                    bgcolor: isCompleted ? "primary.main" : "grey.200",
                    mx: 1,
                    width: 50,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StepperHeader;
